import { Bot as BotIcon, Plus as PlusIcon } from "lucide-react";
import * as React from "react";

import { useAuth } from "@/providers";
import { PageLayout, RobotoLinkHoverUnderline } from "@/shared/components";
import { DevicesTabs } from "@/shared/components/devices";
import {
  renderPrincipal,
  ResourceView,
} from "@/shared/components/ResourceView";
import { type DeviceTypeRecord } from "@/shared/domain/deviceTypes";
import { useAllDeviceTypesByOrg } from "@/shared/domain/deviceTypes/hooks";
import { Banner, Popover, RobotoButton } from "@/shared/foundation/primitives";
import {
  cellStyles,
  colId,
  ColumnVisibility,
  createColumnHelper,
  DataTable,
  deriveLoadingState,
  renderDashOr,
  renderDashOrValue,
  useTable,
  type ColumnPickerOption,
  type ICellRendererParams,
} from "@/shared/foundation/primitives/Table";
import { TagGroup } from "@/shared/foundation/primitives/Tag";
import { Goto } from "@/shared/navigation";

import { CreateDeviceType } from "./CreateDeviceType";
import styles from "./DeviceTypes.module.css";

const columnHelper = createColumnHelper<DeviceTypeRecord>();

/** Module scope, so AG Grid sees the same column objects on every render. */
const columns = [
  columnHelper.accessor("name", {
    headerName: "Name",
    flex: 2,
    minWidth: 180,
    sortable: true,
    hide: false,
    cellRenderer: (p: ICellRendererParams<DeviceTypeRecord, string>) =>
      p.data ? (
        <RobotoLinkHoverUnderline
          to={Goto.createDeviceTypeRoute(p.data.device_type_id)}
          className={cellStyles.primaryContent}
        >
          {p.data.name}
        </RobotoLinkHoverUnderline>
      ) : null,
  }),
  columnHelper.accessor("description", {
    headerName: "Description",
    flex: 3,
    minWidth: 160,
    hide: false,
    cellRenderer: renderDashOrValue,
  }),
  columnHelper.accessor("device_count", {
    headerName: "Devices",
    width: 120,
    sortable: true,
    hide: false,
  }),
  columnHelper.accessor("tags", {
    headerName: "Tags",
    flex: 2,
    minWidth: 160,
    hide: false,
    cellClass: cellStyles.tagsCell,
    valueFormatter: (p) => (p.value ?? []).join(", "),
    cellRenderer: renderDashOr(
      (p: ICellRendererParams<DeviceTypeRecord, string[]>) => (
        <TagGroup tags={p.value ?? []} maxDisplayCount={3} />
      ),
    ),
  }),
  columnHelper.timestamp("created", {
    headerName: "Created",
    minWidth: 150,
    sortable: true,
    hide: false,
    variant: "relative",
  }),
  columnHelper.accessor("created_by", {
    headerName: "Created By",
    minWidth: 150,
    hide: false,
    cellRenderer: renderPrincipal,
  }),
  columnHelper.timestamp("modified", {
    headerName: "Modified",
    minWidth: 150,
    sortable: true,
    hide: false,
    variant: "relative",
  }),
  columnHelper.accessor("modified_by", {
    headerName: "Modified By",
    minWidth: 150,
    hide: false,
    cellRenderer: renderPrincipal,
  }),
];

/** Shown on a first visit; the column picker offers the rest. */
const DEFAULT_VISIBLE_COLUMN_IDS = [
  "name",
  "description",
  "device_count",
  "tags",
  "created",
  "modified",
];

const PICKER_OPTIONS: ColumnPickerOption[] = columns.map((column) => ({
  id: colId(column),
  label: column.headerName ?? colId(column),
}));

/**
 * How each sortable column orders two types. The org's types are fetched in
 * full with no sort parameter, and `<DataTable>` leaves row order to its
 * caller, so a header without an entry here would move its arrow and reorder
 * nothing; keep this map and the `sortable` flags above in step.
 */
const COMPARATORS: Record<
  string,
  (a: DeviceTypeRecord, b: DeviceTypeRecord) => number
> = {
  name: (a, b) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
  device_count: (a, b) => a.device_count - b.device_count,
  created: (a, b) => Date.parse(a.created) - Date.parse(b.created),
  modified: (a, b) => Date.parse(a.modified) - Date.parse(b.modified),
};

/**
 * The Device Types page: a table of every device type in the org, and a button
 * that opens the New Device Type form.
 */
export function DeviceTypes() {
  const { currentOrganization } = useAuth();
  const deviceTypesQuery = useAllDeviceTypesByOrg(currentOrganization?.org_id);
  const table = useTable<DeviceTypeRecord>({
    getRowId: (row) => row.device_type_id,
    initialSort: { columnId: "name", direction: "asc" },
    defaultVisibleColumnIds: DEFAULT_VISIBLE_COLUMN_IDS,
  });

  const sorted = React.useMemo(() => {
    const all = deviceTypesQuery.data ?? [];
    const sort = table.sort;
    const compare = sort === null ? undefined : COMPARATORS[sort.columnId];
    if (sort === null || compare === undefined) {
      return all;
    }
    const factor = sort.direction === "asc" ? 1 : -1;
    return [...all].sort((a, b) => compare(a, b) * factor);
  }, [deviceTypesQuery.data, table.sort]);

  // All of the org's types are loaded at once, so the table's current page is
  // cut from the sorted list in the browser rather than fetched.
  const pageStart = table.currentPage * table.pageSize;
  const rows = sorted.slice(pageStart, pageStart + table.pageSize);

  return (
    <PageLayout title="Roboto - Device Types" headerContentLeft="Devices">
      <div className={styles.content}>
        <DevicesTabs activeTab="device-types" />
        {deviceTypesQuery.error && (
          <Banner variant="error" className={styles.errorBanner}>
            {deviceTypesQuery.error.message}
          </Banner>
        )}
        <div className={styles.toolbar}>
          <ColumnVisibility
            options={PICKER_OPTIONS}
            visibleColumnIds={table.visibleColumnIds}
            setColumnVisibility={table.setColumnVisibility}
          />
          <CreateDeviceTypeButton />
        </div>
        <div className={styles.table}>
          <ResourceView.Root>
            <DataTable<DeviceTypeRecord>
              table={table}
              totalRowCount={sorted.length}
              hasNextPage={pageStart + table.pageSize < sorted.length}
              loading={deriveLoadingState(deviceTypesQuery)}
              columns={columns}
              data={rows}
              noRowsContent={
                <div className={styles.emptyState}>
                  <BotIcon
                    className={styles.emptyStateIcon}
                    aria-hidden="true"
                  />
                  <div className={styles.emptyStateText}>
                    <p className={styles.emptyStateHeading}>
                      No device types yet.
                    </p>
                    <p className={styles.emptyStateSubText}>
                      Create one to share configurations across devices of the
                      same kind.
                    </p>
                  </div>
                  <CreateDeviceTypeButton variant="secondary" align="center" />
                </div>
              }
            />
          </ResourceView.Root>
        </div>
      </div>
    </PageLayout>
  );
}

/** Opens the New Device Type form in a popover; the form closes it on success. */
function CreateDeviceTypeButton({
  variant = "primary",
  align = "end",
}: {
  variant?: "primary" | "secondary";
  /** Where the popover lines up with the button. */
  align?: "center" | "end";
}) {
  const [open, setOpen] = React.useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Popover.Trigger
        render={
          <RobotoButton
            variant={variant}
            icon={<PlusIcon aria-hidden="true" />}
          >
            New Device Type
          </RobotoButton>
        }
      />
      <Popover.Content align={align} aria-label="Create device type">
        <CreateDeviceType close={() => setOpen(false)} />
      </Popover.Content>
    </Popover>
  );
}
